import React, { useState } from 'react';
import { Plus, Trash2, Calculator } from 'lucide-react';
import { Course } from '../types/grades';
import { validateAndParseCredit } from '../utils/creditHandling';
import { useUniversity } from '../contexts/UniversityContext';
import { Card } from './layout/Card';

interface QuickGPACalculatorProps {
  onCalculate: (gpa: number) => void;
}

export function QuickGPACalculator({ onCalculate }: QuickGPACalculatorProps) {
  const { selectedUniversity } = useUniversity();
  const [previousGPA, setPreviousGPA] = useState('');
  const [previousCredits, setPreviousCredits] = useState('');
  const [courses, setCourses] = useState<Course[]>([
    { code: '', credits: 0, grade: '' }
  ]);

  const handleCalculate = () => {
    const prevGPA = parseFloat(previousGPA) || 0;
    const prevCredits = parseInt(previousCredits) || 0;
    
    let totalPoints = prevGPA * prevCredits;
    let totalCredits = prevCredits;

    courses.forEach(course => {
      if (course.credits && course.grade) {
        const gradePoints = selectedUniversity.gradeScale.find(g => g.grade === course.grade)?.points || 0;
        totalPoints += course.credits * gradePoints;
        totalCredits += course.credits;
      }
    });

    const finalGPA = totalCredits > 0 ? totalPoints / totalCredits : 0;
    onCalculate(finalGPA);
  };

  return (
    <Card>
      <h2 className="text-xl font-bold mb-6">Quick GPA Calculator</h2>
      
      <div className="space-y-8">
        {/* Previous GPA Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Previous GPA (if any)
            </label>
            <input
              type="number"
              value={previousGPA}
              onChange={(e) => setPreviousGPA(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              step="0.01"
              min="0"
              max="4"
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Previous Credits
            </label>
            <input
              type="number"
              value={previousCredits}
              onChange={(e) => setPreviousCredits(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              min="0"
              placeholder="0"
            />
          </div>
        </div>

        {/* Course List */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-800">New Courses</h3>
          {courses.map((course, index) => (
            <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end bg-gray-50 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Code
                </label>
                <input
                  type="text"
                  value={course.code}
                  onChange={(e) => {
                    const newCourses = [...courses];
                    newCourses[index] = { ...course, code: e.target.value.toUpperCase() };
                    setCourses(newCourses);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. MATH101"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Credits
                </label>
                <input
                  type="text"
                  value={course.credits || ''}
                  onChange={(e) => {
                    const parsedValue = validateAndParseCredit(e.target.value);
                    const newCourses = [...courses];
                    newCourses[index] = { ...course, credits: parsedValue ?? 0 };
                    setCourses(newCourses);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  placeholder="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Grade
                </label>
                <select
                  value={course.grade}
                  onChange={(e) => {
                    const newCourses = [...courses];
                    newCourses[index] = { ...course, grade: e.target.value };
                    setCourses(newCourses);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Grade</option>
                  {selectedUniversity.gradeScale.map(grade => (
                    <option key={grade.grade} value={grade.grade}>
                      {grade.grade} ({grade.minScore}-{grade.maxScore}%)
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex justify-end">
                {courses.length > 1 && (
                  <button
                    onClick={() => setCourses(courses.filter((_, i) => i !== index))}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                    title="Remove course"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => setCourses([...courses, { code: '', credits: 0, grade: '' }])}
            className="flex items-center justify-center gap-2 px-4 py-2 text-blue-600 border border-blue-300 
                     rounded-md hover:bg-blue-50 transition-colors"
          >
            <Plus size={20} />
            Add Course
          </button>
          <button
            onClick={handleCalculate}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white 
                     rounded-md hover:bg-blue-700 transition-colors"
          >
            <Calculator size={20} />
            Calculate GPA
          </button>
        </div>
      </div>
    </Card>
  );
}